<script>
  import { InputTypes } from './Input/enums';
  import Label from './Label.svelte';
  import Tooltip from './Tooltip.svelte';
  import { getLanguageInfo, SupportedLanguages } from '../common/localization';
  import Icon from './Icon/Icon.svelte';
  import { Icons } from './Icon/Icons';
  import { Rounding } from './enums';

  export let value;
  export let label;
  export let infoText = null;
  export let classes = "";
  export let type = InputTypes.Decimal;
  export let language = SupportedLanguages.English;
  export let placeholder = "";
  export let rounding = Rounding.Full;
  export let showIcon = true;
  export let onChange = null;
  export let disabled = false;

  const inputId = `input-${label.trim()}`;

  const languageInfo = getLanguageInfo(language);

  let focused = false;

  function handleFocusIn() { focused = true; }
  function handleFocusOut() { focused = false; }
  function handleChange(evt) {
    let newValue;
    switch (type) {
      case InputTypes.Decimal:
        const adjustedString = evt.target.value
          .replaceAll(languageInfo.thousandsDelimiter, "")
          .replaceAll(languageInfo.decimalDelimiter, getLanguageInfo("default").decimalDelimiter);
        const parsedValue = evt.target.value != "" ? Number(adjustedString) : 0;
        newValue = isNaN(parsedValue) ? 0 : parsedValue;
        break;
      default:
        newValue = evt.target.value;
    }
    value = newValue;

    onChange && onChange();
  }

  $: roundingClass = {
    [Rounding.Full]: "rounded",
    [Rounding.Left]: "rounded-l rounded-r-none",
    [Rounding.Right]: "rounded-r rounded-l-none",
    [Rounding.None]: "rounded-none"
  }[rounding]

  $: wrapperFocusedClasses = focused ? "border-primary-400 ring-2 ring-primary-300 border-opacity-50" : "border-gray-500 hover:border-gray-600 border-opacity-75";
  $: wrapperClasses = `flex items-center border h-10 pl-3.5 ${roundingClass} ${wrapperFocusedClasses}`;

  $: displayValue = type === InputTypes.Decimal ? value.toLocaleString(languageInfo.locale) : value;
</script>

<div class="{classes}">
  <div class="flex items-center">
    <Label text={label} forId={inputId} />
    {#if infoText}
      <Tooltip content={infoText}>
        <svg
          class="inline-block fill-current w-4 h-4 text-gray-800 ml-1"
          focusable="false"
          viewBox="0 0 24 24"
        >
          <path fill-rule="evenodd" clip-rule="evenodd" d="M12 3.81818C7.48131 3.81818 3.81818 7.48131 3.81818 12C3.81818 16.5187 7.48131 20.1818 12 20.1818C16.5187 20.1818 20.1818 16.5187 20.1818 12C20.1818 7.48131 16.5187 3.81818 12 3.81818ZM2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12ZM12 11.0909C12.5021 11.0909 12.9091 11.4979 12.9091 12V15.6364C12.9091 16.1384 12.5021 16.5455 12 16.5455C11.4979 16.5455 11.0909 16.1384 11.0909 15.6364V12C11.0909 11.4979 11.4979 11.0909 12 11.0909ZM12 7.45455C11.4979 7.45455 11.0909 7.86156 11.0909 8.36364C11.0909 8.86571 11.4979 9.27273 12 9.27273H12.0091C12.5112 9.27273 12.9182 8.86571 12.9182 8.36364C12.9182 7.86156 12.5112 7.45455 12.0091 7.45455H12Z" />
        </svg>
      </Tooltip>
    {/if}
  </div>
  <div class={wrapperClasses}>
    {#if showIcon}
      {#if type === InputTypes.Decimal}
        <svg
          class="inline-block fill-current text-gray-800 w-4 h-4"
          focusable="false"
          viewBox="0 0 24 24"
        >
          {#if languageInfo.currency == "eur"}
            <path fill-rule="evenodd" clip-rule="evenodd" d="M7.582 9C8.77 6.069 11.645 4 15 4C16.7 4 18.276 4.531 19.572 5.436C20.025 5.752 20.136 6.376 19.82 6.829C19.504 7.281 18.88 7.392 18.428 7.076C17.456 6.398 16.274 6 15 6C12.781 6 10.842 7.207 9.804 9H15C15.552 9 16 9.448 16 10C16 10.552 15.552 11 15 11H9.083C9.028 11.325 9 11.659 9 12C9 12.341 9.028 12.675 9.083 13H15C15.552 13 16 13.448 16 14C16 14.552 15.552 15 15 15H9.804C10.842 16.793 12.781 18 15 18C16.274 18 17.456 17.602 18.428 16.924C18.88 16.608 19.504 16.719 19.82 17.171C20.136 17.624 20.025 18.248 19.572 18.564C18.276 19.469 16.7 20 15 20C11.645 20 8.77 17.931 7.582 15H5C4.448 15 4 14.552 4 14C4 13.448 4.448 13 5 13H7.062C7.021 12.672 7 12.339 7 12C7 11.661 7.021 11.328 7.062 11H5C4.448 11 4 10.552 4 10C4 9.448 4.448 9 5 9H7.582Z"></path>
          {:else}
            <path d="M12 0C12.5523 0 13 0.447715 13 1V23C13 23.5523 12.5523 24 12 24C11.4477 24 11 23.5523 11 23V1C11 0.447715 11.4477 0 12 0Z M6.31802 5.31802C7.16193 4.47411 8.30653 4 9.5 4H17C17.5523 4 18 4.44772 18 5C18 5.55228 17.5523 6 17 6H9.5C8.83696 6 8.20107 6.26339 7.73223 6.73223C7.26339 7.20107 7 7.83696 7 8.5C7 9.16304 7.26339 9.79893 7.73223 10.2678C8.20107 10.7366 8.83696 11 9.5 11H14.5C15.6935 11 16.8381 11.4741 17.682 12.318C18.5259 13.1619 19 14.3065 19 15.5C19 16.6935 18.5259 17.8381 17.682 18.682C16.8381 19.5259 15.6935 20 14.5 20H6C5.44772 20 5 19.5523 5 19C5 18.4477 5.44772 18 6 18H14.5C15.163 18 15.7989 17.7366 16.2678 17.2678C16.7366 16.7989 17 16.163 17 15.5C17 14.837 16.7366 14.2011 16.2678 13.7322C15.7989 13.2634 15.163 13 14.5 13H9.5C8.30653 13 7.16193 12.5259 6.31802 11.682C5.47411 10.8381 5 9.69347 5 8.5C5 7.30653 5.47411 6.16193 6.31802 5.31802Z" />
          {/if}
        </svg>
      {:else if type === InputTypes.Text}
        <Icon iconType={Icons.Search} pathFill="#98AFB6" classes="w-4 h-4 inline-block" viewBox="0 0 21 21" />
      {/if}
    {/if}
    <input
      id={inputId}
      type={type}
      value={displayValue}
      on:change={handleChange}
      on:focusin={handleFocusIn}
      on:focusout={handleFocusOut}
      placeholder={placeholder}
      disabled={disabled}
      class="border-0 ring-0 px-0 bg-transparent w-full cursor-inherit font-sans font-medium placeholder-gray-600 focus:outline-none focus:ring-0 border-primary-400 text-base text-gray-950"
    />

    <slot name="end" />
  </div>
</div>